import React, { useState, useEffect, useCallback } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import paymentsService from '../services/payments.service';

const FlujoCaja: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [fechaInicio, setFechaInicio] = useState(() => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() - 3); // 3 meses atrás
    return fecha.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const fecha = new Date();
    fecha.setMonth(fecha.getMonth() + 3); // 3 meses adelante
    return fecha.toISOString().split('T')[0];
  });

  const fetchFlujoCaja = useCallback(async () => {
    setLoading(true);
    try {
      const response = await paymentsService.getFlujoCaja(fechaInicio, fechaFin);
      setData(response);
    } catch (error) {
      console.error('Error fetching flujo de caja:', error);
      alert('Error al cargar el flujo de caja');
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    fetchFlujoCaja();
  }, [fetchFlujoCaja]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchFlujoCaja();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <ChartBarIcon className="h-8 w-8 text-primary-500 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Flujo de Caja</h1>
            <p className="mt-2 text-sm text-gray-700">
              Comparación entre pagos programados y pagos reales
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Inicio
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Fin
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
          >
            {loading ? 'Cargando...' : 'Consultar'}
          </button>
        </form>
      </div>

      {data && (
        <>
          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                Total Programado
              </div>
              <div className="mt-2 text-2xl font-bold text-blue-600">
                {formatCurrency(data.resumen.totalProgramado)}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                Total Real
              </div>
              <div className="mt-2 text-2xl font-bold text-green-600">
                {formatCurrency(data.resumen.totalReal)}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                Diferencia
              </div>
              <div className={`mt-2 text-2xl font-bold ${
                data.resumen.diferencia >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(data.resumen.diferencia)}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                % Cumplimiento
              </div>
              <div className={`mt-2 text-2xl font-bold ${
                data.resumen.porcentajeCumplimiento >= 100 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {data.resumen.porcentajeCumplimiento.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Flujo por mes */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Flujo de Caja Mensual</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Programado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Real
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Diferencia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % Cumplimiento
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.pagosPorMes.map((mes: any) => {
                    const cumplimiento = mes.programado > 0 ? (mes.real / mes.programado * 100) : 0;
                    return (
                      <tr key={mes.mes} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatMonth(mes.mes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                          {formatCurrency(mes.programado)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          {formatCurrency(mes.real)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                          mes.diferencia >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(mes.diferencia)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                          cumplimiento >= 100 ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {cumplimiento.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detalles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pagos Programados */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Pagos Programados ({data.pagosProgramados.length})
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.pagosProgramados.map((pago: any) => (
                      <tr key={pago.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {new Date(pago.fecha).toLocaleDateString('es-CO')}
                          {pago.pagoRealAsociado && (
                            <div className="text-xs text-green-600 mt-1">
                              Real: {new Date(pago.pagoRealAsociado.fechaPago).toLocaleDateString('es-CO')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {pago.cliente}
                        </td>
                        <td className="px-4 py-3 text-xs text-blue-600">
                          {formatCurrency(pago.monto)}
                          {pago.pagoRealAsociado && pago.pagoRealAsociado.monto !== pago.monto && (
                            <div className="text-xs text-green-600 mt-1">
                              Real: {formatCurrency(pago.pagoRealAsociado.monto)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pago.estado === 'pagado'
                              ? 'bg-green-100 text-green-800'
                              : pago.estado === 'cancelado'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {pago.estado}
                          </span>
                          {pago.pagoRealAsociado && (
                            <div className="text-xs text-gray-500 mt-1">
                              {pago.pagoRealAsociado.metodoPago}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagos Reales */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Pagos Reales ({data.pagosReales.length})
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Método
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.pagosReales.map((pago: any) => (
                      <tr key={pago.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {new Date(pago.fechaPago).toLocaleDateString('es-CO')}
                          {pago.pagoProgramadoAsociado && (
                            <div className="text-xs text-blue-600 mt-1">
                              Programado: {new Date(pago.pagoProgramadoAsociado.fecha).toLocaleDateString('es-CO')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900">
                          {pago.cliente}
                          {pago.pagoProgramadoAsociado && (
                            <div className="text-xs text-blue-500 mt-1">
                              {pago.pagoProgramadoAsociado.descripcion || 'Sin descripción'}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-green-600">
                          {formatCurrency(pago.monto)}
                          {pago.pagoProgramadoAsociado && pago.pagoProgramadoAsociado.monto !== pago.monto && (
                            <div className="text-xs text-blue-600 mt-1">
                              Programado: {formatCurrency(pago.pagoProgramadoAsociado.monto)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {pago.metodoPago}
                          {pago.pagoProgramadoId ? (
                            <div className="text-xs text-green-600 mt-1">
                              ✓ Asociado
                            </div>
                          ) : (
                            <div className="text-xs text-orange-600 mt-1">
                              Independiente
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Sin datos</h3>
          <p className="mt-1 text-sm text-gray-500">
            Selecciona un rango de fechas para ver el flujo de caja.
          </p>
        </div>
      )}
    </div>
  );
};

export default FlujoCaja;